import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete, Inventory2Outlined, RefreshOutlined } from '@mui/icons-material';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import { useAdminProductsQuery } from '@/state/queries/admin/use-admin-products-query';
import { useDeleteProductMutation } from '@/state/mutations/admin-products/use-delete-product-mutation';
import ProductAdminFormModal from '@/components/admin-products/product-admin-form.modal';
import type { ProductDto } from '@/types/dtos/orders/response/product.dto';
import { PRODUCT_KINDS } from '@/types/dtos/orders/response/product.dto';

const SCOPE_FILTER = ['', 'Event', 'Organization', 'Flow', 'Venue'] as const;
const KIND_FILTER = ['', PRODUCT_KINDS.QUOTA, PRODUCT_KINDS.FEATURE, PRODUCT_KINDS.PACKAGE] as const;

function formatMoney(n: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(n);
}

export default function ProductsAdminPage() {
  const [filterKind, setFilterKind] = useState('');
  const [filterScope, setFilterScope] = useState('');
  const {
    data: products,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useAdminProductsQuery({
    kind: filterKind || undefined,
    scopeType: filterScope || undefined,
  });
  const deleteMut = useDeleteProductMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProductDto | null>(null);

  const handleDelete = async (p: ProductDto) => {
    if (!window.confirm(`Soft-delete product “${p.name}” (${p.key})?`)) return;
    try {
      await deleteMut.mutateAsync(p.id);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PageHeaderBar
            mainContent={
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Inventory2Outlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Product catalog (admin)
                  </Typography>
                  <IconButton size="small" aria-label="Refresh" onClick={() => refetch()} disabled={isFetching}>
                    {isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
                  </IconButton>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setEditing(null);
                    setModalOpen(true);
                  }}
                >
                  Create product
                </Button>
              </Box>
            }
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2, alignItems: { sm: 'center' } }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="filter-kind">Kind</InputLabel>
              <Select
                labelId="filter-kind"
                label="Kind"
                value={filterKind}
                onChange={(e) => setFilterKind(e.target.value)}
              >
                <MenuItem value="">All kinds</MenuItem>
                {KIND_FILTER.filter(Boolean).map((k) => (
                  <MenuItem key={k} value={k}>
                    {k}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="filter-scope">Scope type</InputLabel>
              <Select
                labelId="filter-scope"
                label="Scope type"
                value={filterScope}
                onChange={(e) => setFilterScope(e.target.value)}
              >
                <MenuItem value="">All scopes</MenuItem>
                {SCOPE_FILTER.filter(Boolean).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Card sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 0 }}>
              {error && (
                <Alert severity="error" sx={{ m: 2 }}>
                  {(error as Error).message}
                </Alert>
              )}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!isLoading && !products?.length && (
                <EmptyState text='No products yet. Use "Create product" to add one.' />
              )}
              {!isLoading && !!products?.length && (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Key</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Kind</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell>Scheduled</TableCell>
                        <TableCell>Validity</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((p) => (
                        <TableRow key={p.id} hover sx={{ opacity: p.deleted ? 0.5 : 1 }}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {p.key}
                            </Typography>
                            {p.deleted && <Chip size="small" label="Deleted" color="warning" sx={{ mt: 0.5 }} />}
                          </TableCell>
                          <TableCell>{p.name}</TableCell>
                          <TableCell>
                            <Chip size="small" label={p.kind} variant="outlined" />
                          </TableCell>
                          <TableCell>{p.scopeType}</TableCell>
                          <TableCell align="right">{formatMoney(p.pricePerUnit)}</TableCell>
                          <TableCell>
                            {p.scheduledPrice ? (
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={`${formatMoney(p.scheduledPrice.pricePerUnit)} @ ${new Date(p.scheduledPrice.effectiveFrom).toLocaleString()}`}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              {p.validFrom ? new Date(p.validFrom).toLocaleDateString() : '—'} →{' '}
                              {p.validUntil ? new Date(p.validUntil).toLocaleDateString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              aria-label="Edit"
                              onClick={() => {
                                setEditing(p);
                                setModalOpen(true);
                              }}
                              disabled={p.deleted}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Delete"
                              onClick={() => handleDelete(p)}
                              disabled={p.deleted || deleteMut.isPending}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <ProductAdminFormModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setEditing(null);
            }}
            product={editing}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, px: 1 }}>
            Requires admin role. Price changes on edit follow server rules (price history, scheduled price).
          </Typography>
        </Box>
      }
    />
  );
}
